import { Stack } from 'component-library';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { GraphTileType } from '@/api/rest/resources/types/widget';
import { AboveGroundCarbonStorageHistorical } from '@/components/Charts';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { ItemizedGraphTypeRenderer } from '@/components/Charts/components/ItemizedGraphType';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { GraphFact } from '@/components/Charts/types';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useSetIndicatorHasData } from '@/components/IndicatorCard/IndicatorContextProvider';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { useAboveGroundCarbonFacts } from '@/hooks/useAboveGroundCarbonFacts';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useScaledCustomBenchmark } from '@/hooks/useScaledCustomBenchmark';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';

import { NoData } from '../../../../components/NoData';

export const AboveGroundCarbonCardGraphTile = (
  props?: (GraphTileProps & { selectedBenchmark?: string | null }) | null,
) => {
  const { t } = useTranslation();
  const { analysisType } = useNCCardContext();
  const isTotal = analysisType === 'total';

  const { setIndicatorGraphData } = useSetIndicatorHasData();

  const { zoom, setZoom } = useChartZoom();
  const selectedZoom = props?.zoom ?? zoom; // props.zoom is used when this component is rendered as widget (e.g. measurement pages)
  const setZoomFunc = props?.setZoom ?? setZoom;
  const plotGroupId = props?.plotGroupId ?? null;

  const facts = useAboveGroundCarbonFacts();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const carbonAgHistoricFact = isTotal ? facts!.total.graph : facts!.perHa.graph;
  const { [carbonAgHistoricFact]: carbonAgHistoric } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    carbonAgHistoricFact,
    null,
    undefined,
    plotGroupId,
  );
  const carbonAgHistoricData = carbonAgHistoric?.value;

  // const carbonAgLowBenchmarkFact =
  //   analysisType === 'total'
  //     ? facts.total.benchmark
  //     : facts.perHa.benchmark;
  // const { [carbonAgLowBenchmarkFact]: carbonAgLowBenchmark } = useOrganizationIndicatorGraphFact<GraphFact[]>(
  //   carbonAgLowBenchmarkFact,
  //   null,
  //   { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low },
  //   plotGroupId,
  // );
  // const cagLowBenchmarkData = (carbonAgLowBenchmark?.value ?? []).map((fact) => {
  //   return { ...fact, name: `${fact.name}_low` };
  // });

  // const carbonAgModerateBenchmarkFact =
  //   analysisType === 'total'
  //     ? facts.total.benchmark
  //     : facts.perHa.benchmark;
  // const { [carbonAgModerateBenchmarkFact]: carbonAgModerateBenchmark } = useOrganizationIndicatorGraphFact<GraphFact[]>(
  //   carbonAgModerateBenchmarkFact,
  //   null,
  //   {
  //     [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
  //   },
  //   plotGroupId,
  // );
  // const cagModerateBenchmarkData = (carbonAgModerateBenchmark?.value ?? []).map((fact) => {
  //   return { ...fact, name: `${fact.name}_moderate` };
  // });

  // const carbonHighBenchmarkFact =
  //   analysisType === 'total'
  //     ? facts.total.benchmark
  //     : facts.perHa.benchmark;
  // const { [carbonHighBenchmarkFact]: carbonHighBenchmark } = useOrganizationIndicatorGraphFact<GraphFact[]>(
  //   carbonHighBenchmarkFact,
  //   null,
  //   {
  //     [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
  //   },
  //   plotGroupId,
  // );
  // const cagHighBenchmarkData = (carbonHighBenchmark?.value ?? []).map((fact) => {
  //   return { ...fact, name: `${fact.name}_high` };
  // });

  // const cagBenchmarks = useBenchmarksForOrganization(
  //   analysisType === 'total'
  //     ? facts.total.benchmark
  //     : facts.perHa.benchmark,
  //   null,
  //   kgToTonneFormatter,
  //   plotGroupId,
  // );

  const selectedBenchmark = props?.selectedBenchmark;

  const organizationId = useOrganizationId();
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(
    organizationId,
    GraphTileType.historic_above_ground_carbon,
  );

  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmark);
  const displayCustomBenchmark = useScaledCustomBenchmark(activeCustomBenchmark, isTotal);

  const noChartData = !carbonAgHistoricData?.length;

  useEffect(() => {
    setIndicatorGraphData({ graphData: !noChartData });
  }, [noChartData, setIndicatorGraphData]);

  if (noChartData) {
    return <NoData />;
  }

  const chartData = [
    ...carbonAgHistoricData,
    // ...cagLowBenchmarkData,
    // ...cagModerateBenchmarkData,
    // ...cagHighBenchmarkData,
  ];

  return (
    <Stack center data-testid='carbon-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        {!props?.headerProps?.hideChartHeader && (
          <ChartHeading
            heading={t('shared.ncaDetail.details.carbon.labels.historicalAboveGroundGraphHeadline')}
            popover={{
              title: t(
                'shared.ncaDetail.details.aboveGroundCarbon.explainers.HistoricalAboveGroundCarbonStorage.title',
              ),
              body: t('shared.ncaDetail.details.aboveGroundCarbon.explainers.HistoricalAboveGroundCarbonStorage.body'),
            }}
            className='w-full px-6 sm:px-0'
            zoom={selectedZoom}
            setZoom={setZoom}
          />
        )}
        <ItemizedGraphTypeRenderer
          graphType={props?.graphType}
          indicator={GraphTileType.historic_above_ground_carbon}
          plotRenderType={props?.plotRenderType ?? NUM_ITEMS_TYPE.TOP_PLOTS}
          zoom={selectedZoom}
          setZoom={setZoomFunc}
          customBenchmark={displayCustomBenchmark}
          defaultGraphType={
            <AboveGroundCarbonStorageHistorical
              analysisType={analysisType}
              data={chartData}
              // benchmarks={cagBenchmarks}
              showTooltip={true}
              className='relative'
              data-testid='above-ground-carbon-storage-historic-chart'
              zoom={selectedZoom}
              setZoom={setZoomFunc}
              customBenchmark={displayCustomBenchmark}
            />
          }
        />
      </Stack>
    </Stack>
  );
};
